import * as React from 'react'
import { i18n as globalI18n, type I18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { useLingui } from '@lingui/react/macro'
import { useRouterState, type ErrorComponentProps } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { basename, cn } from './lib/utils'
import { withSdkParams } from './lib/sdk-params'
import { resolveRedirectTarget } from './lib/redirect-target'
import { firstIncompleteTestPath, trpc } from './main'
import { classifyRouteError, isChunkLoadError, type RouteErrorUser } from './route-error-classify'
import { isRetryableError } from './lib/query-retry'
import { logFrontendError } from './lib/log-error'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

// Guard against a reload loop when a chunk is GENUINELY gone rather than unreachable: survives the reload via
// sessionStorage, so after one automatic attempt inside the cooldown we stand down and leave the manual button.
const CHUNK_RELOAD_GUARD_KEY = 'danchiano:chunk-reload-at'
const CHUNK_RELOAD_COOLDOWN_MS = 20_000
function reloadedForChunkRecently(): boolean {
  try {
    const at = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)
    return !!at && Date.now() - Number(at) < CHUNK_RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

// Is the server actually reachable? `navigator.onLine` LIES — a down server, a captive portal and DevTools
// "offline" can all still report online — so probe with a real request. A tiny same-origin HEAD with a
// cache-buster, so it's never answered from the HTTP cache and reflects the true state of the network.
async function serverReachable(): Promise<boolean> {
  try {
    const res = await fetch(`${basename}/favicon.ico?connectivity=${Date.now()}`, { method: 'HEAD', cache: 'no-store' })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Router-wide error boundary (wired as `defaultErrorComponent`).
 *
 * Components now read data with `useSuspenseQuery` (getUser, getReport, getMyEmployers, …). A suspense
 * query THROWS its error during render, so an error on a streamed query — e.g. an anonymous or
 * expired-session load of `/report` — would otherwise crash the whole tree (React #418) instead of
 * redirecting. We catch it per-route (so the server-rendered chrome stays) and decide what to do:
 *
 *   - No logged-in user (the getUser cache is null) → redirect to /login. This is the auth/expired
 *     case. We key off the user, NOT the error code, because a DEFERRED-streamed query's rejection has
 *     its message/code REDACTED by TanStack Start in production — so the thrown error often arrives as
 *     a bare "redacted" with no code, and only the absent session tells us it's an auth problem.
 *   - TEST_INCOMPLETE (message survives for non-streamed throws) → redirect to the first unfinished
 *     questionnaire (resume point), mirroring the client toastErrorLink in main.tsx.
 *   - Otherwise (a logged-in user hit a real error) → a graceful, non-crashing fallback.
 */
export function DefaultRouteError(props: ErrorComponentProps & { i18n?: I18n }) {
  // This is the router's `defaultErrorComponent`, so it's also the boundary for the ROOT route — and a
  // root-level error (e.g. a streamed getApp/getUser rejection thrown by AppConfigSync/AppUserSync)
  // renders it ABOVE AppShell's <I18nProvider>, where `useLingui` would throw "used without
  // I18nProvider" and blank the whole view (notably an SDK embed). Provide our own provider — the
  // `i18n` singleton is already loaded with the active catalog — so the boundary is self-sufficient.
  // Nesting under the app-level provider in the normal (child-route) case is harmless. Prefer the
  // request-scoped instance passed by the router (safe from concurrent SSR renders — React #418); fall
  // back to the shared global for callers that don't pass one. See createLocaleI18n / router.tsx.
  return (
    <I18nProvider i18n={props.i18n ?? globalI18n}>
      <RouteErrorContent {...props} />
    </I18nProvider>
  )
}

function RouteErrorContent({ error, reset }: ErrorComponentProps) {
  const { t } = useLingui()
  const queryClient = useQueryClient()
  const routeId = useRouterState({ select: (s) => s.matches[s.matches.length - 1]?.routeId ?? '' })
  // Duck-type rather than `instanceof TRPCClientError`: bundling can give the thrown error a different
  // class identity than the one imported here, so `instanceof` is unreliable across module boundaries.
  const err = error as { data?: { code?: string }; message?: string } | undefined
  const message = err?.message
  // No session → treat the error as auth (redirect to login) — but ONLY on an auth-required route, so
  // an error on a PUBLIC report (e.g. /market) never forces an anonymous viewer to log in. getUser is
  // authOptional and resolves to null when logged out, so an absent cached user means anonymous/expired.
  // (A streamed query's rejection is redacted in prod, so we key off the missing session, not the code.)
  const cachedUser = queryClient.getQueryData(trpc.auth.getUser.queryKey()) as RouteErrorUser | null
  // An applicant who hasn't finished the three questionnaires can't load a test-gated view
  // (report/teams/jobs): those reads reject with TEST_INCOMPLETE. The thrown message survives for a
  // NON-streamed throw, but a DEFERRED-streamed query's rejection is REDACTED in production — so the
  // error often arrives codeless. We infer the case from the CACHED user (a logged-in applicant with
  // the test still pending) on a test-gated route, instead of trusting the message. See classifyRouteError.
  const { isAuth, isTestIncomplete } = classifyRouteError({ routeId, cachedUser, message })

  // A transient failure (transport drop, 5xx/408/429) or a failed CODE chunk auto-recovers instead of showing a
  // hard error — and reports itself only when it turns out to be a real defect. See RecoverableErrorNotice.
  const recoverable = isChunkLoadError(error) || isRetryableError(error)

  // Report genuine crashes to the backend so they surface in the server log (CloudWatch/SST) — once
  // per boundary mount. Auth/test cases are normal redirect control-flow, not bugs, so skip them.
  const hasLogged = React.useRef(false)
  React.useEffect(() => {
    if (isAuth || isTestIncomplete || recoverable || hasLogged.current) return
    hasLogged.current = true
    logFrontendError(error)
  }, [error, isAuth, isTestIncomplete, recoverable])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (isAuth) {
      // resolveRedirectTarget unwraps the current URL so an auth error surfacing while already on /login
      // reuses the real destination instead of nesting /login into a fresh (re-encoded) ?redirect=.
      const target = resolveRedirectTarget(window.location.pathname + window.location.search)
      const query = target ? `?redirect=${encodeURIComponent(target)}` : ''
      window.location.replace(withSdkParams(`${basename}/login${query}`))
    } else if (isTestIncomplete && !window.location.pathname.includes('/test')) {
      // Resume at the first unfinished questionnaire (competences → values → personality), mirroring
      // the client toastErrorLink in main.tsx — not a bare /test, which doesn't auto-forward. Carry the
      // API/SDK token-link session params so a token-link visitor isn't logged out by the hard redirect.
      window.location.replace(withSdkParams(`${basename}${firstIncompleteTestPath()}`))
    }
  }, [isAuth, isTestIncomplete])

  // Auth/test errors are mid-redirect — render nothing rather than flash an error.
  if (isAuth || isTestIncomplete) return null

  if (recoverable) return <RecoverableErrorNotice error={error} reset={reset} />

  return (
    <div className="flex min-h-[40dvh] w-full flex-col items-center justify-center gap-3 p-8 text-center">
      <p className="text-foreground text-base font-semibold">{t`Something went wrong`}</p>
      <p className="text-muted-foreground max-w-sm text-sm">{t`We couldn’t load this page. Please try again.`}</p>
      <ButtonPrimitive
        type="button"
        onClick={() => window.location.reload()}
        className={cn('bg-primary text-primary-foreground mt-1 rounded-md px-4 py-2 text-sm font-medium')}
      >
        {t`Try again`}
      </ButtonPrimitive>
    </div>
  )
}

/**
 * Auto-recovery for a failure a retry could actually fix (isRetryableError): a transport drop, a 5xx/408/429, or
 * a route's CODE chunk that wouldn't import. On a CLIENT navigation there is no SSR data to fall back on, so a
 * hard error would strand the user on a page that would work a second later.
 *
 * Recovery differs by kind, which is the whole reason the chunk case is detected separately:
 *  - a DATA error refetches in place (`reset()`) — cheap, keeps the page.
 *  - a CHUNK error can only be fixed by a full reload: the browser caches the failed import, so re-rendering
 *    returns the same cached rejection forever.
 *
 * Either way we wait for the server to actually ANSWER first. Reloading or refetching blind is what blanked the
 * page offline: the reload landed on a dead `chrome-error://` document. Until then the chrome stays up with an
 * honest message.
 *
 * Reporting is narrowed to failures that are genuinely defects, because every report here auto-files an issue.
 * An unreachable server is the user's network, and a blip that recovers on the first attempt is a blip — neither
 * files anything (between them, the bulk of the auto-filed "Failed to fetch dynamically imported module"
 * reports). What DOES file: the server is reachable and it still won't recover — a chunk that should exist and
 * doesn't, or a 5xx that keeps 5xx-ing. Hashed assets persist on the CDN (upload-to-cdn.ts), which is what makes
 * silence the right default for the chunk half.
 */
// Bounded auto-recovery budget for a persistently-failing but REACHABLE error: after this many attempts that
// re-error, stop the loop, report once, and leave the manual button. A module singleton so it survives the
// remount `reset()` causes; a long enough quiet gap counts as a new incident and clears it. Waiting while
// offline costs nothing — only a real attempt does.
const AUTO_RETRY_LIMIT = 4
const INCIDENT_RESET_MS = 30_000
let autoRetry = { attempts: 0, lastAt: 0 }

function RecoverableErrorNotice({ error, reset }: { error: unknown; reset: () => void }) {
  const { t } = useLingui()
  const chunkError = isChunkLoadError(error)
  // null = still probing; true/false = the last probe's answer. Drives the copy.
  const [reachable, setReachable] = React.useState<boolean | null>(null)
  // Auto-recovery has given up (chunk gone, or the budget is spent): no spinner and no "we'll keep trying",
  // which would be a lie — just the honest copy and the (always-working) manual button.
  const [standingBy, setStandingBy] = React.useState(false)
  const hasLogged = React.useRef(false)

  // oxlint-disable-next-line react-compiler-memo/no-manual-memo -- kept deliberately: an effect or context value here depends on this referential identity — without it react-hooks/exhaustive-deps flags the dependency as changing every render
  const report = React.useCallback(() => {
    if (hasLogged.current) return
    hasLogged.current = true
    logFrontendError(error)
  }, [error])

  // Only ever called once the server is confirmed reachable.
  // oxlint-disable-next-line react-compiler-memo/no-manual-memo -- kept deliberately: an effect or context value here depends on this referential identity — without it react-hooks/exhaustive-deps flags the dependency as changing every render
  const recover = React.useCallback(
    (manual: boolean) => {
      // A manual click is a deliberate user action: it always attempts, and resets the budget so the automatic
      // loop resumes afterwards. A silently dead "Try again" reads as a broken page.
      if (manual) autoRetry = { attempts: 0, lastAt: Date.now() }
      else if (Date.now() - autoRetry.lastAt > INCIDENT_RESET_MS) autoRetry = { attempts: 0, lastAt: Date.now() }

      if (!manual && autoRetry.attempts >= AUTO_RETRY_LIMIT) {
        // Reachable, and it has failed every time we tried: a real backend problem, not a blip.
        setStandingBy(true)
        report()
        return
      }
      autoRetry = { attempts: autoRetry.attempts + 1, lastAt: Date.now() }

      if (!chunkError) {
        reset()
        return
      }
      if (!manual && reloadedForChunkRecently()) {
        // We already spent a reload and the chunk STILL fails with the server up: the asset is genuinely gone.
        setStandingBy(true)
        report()
        return
      }
      try {
        window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()))
      } catch {
        // sessionStorage unavailable (private mode / sandboxed iframe) — reload anyway; worst case is one extra.
      }
      window.location.reload()
    },
    [chunkError, reset, report],
  )

  // Poll the server and recover the instant it answers. Capped backoff (3s → 15s) so a fleet of tabs stranded on
  // one outage doesn't hammer a recovering server, with the `online` event as the fast path.
  React.useEffect(() => {
    if (standingBy) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    let delay = 3000
    const tick = async () => {
      if (cancelled) return
      const ok = await serverReachable()
      if (cancelled) return
      setReachable(ok)
      if (ok) {
        recover(false)
        return
      }
      timer = setTimeout(tick, delay)
      delay = Math.min(delay * 2, 15_000)
    }
    timer = setTimeout(tick, 600)
    const onOnline = () => {
      delay = 3000
      clearTimeout(timer)
      void tick()
    }
    window.addEventListener('online', onOnline)
    return () => {
      cancelled = true
      clearTimeout(timer)
      window.removeEventListener('online', onOnline)
    }
  }, [standingBy, recover])

  // Manual retry probes first too, so it can never reload or refetch into a still-dead network.
  // oxlint-disable-next-line react-compiler-memo/no-manual-memo -- kept deliberately: an effect or context value here depends on this referential identity — without it react-hooks/exhaustive-deps flags the dependency as changing every render
  const retry = React.useCallback(() => {
    setStandingBy(false)
    void serverReachable().then((ok) => {
      setReachable(ok)
      if (ok) recover(true)
    })
  }, [recover])

  const offline = reachable === false
  return (
    <div className="flex min-h-[40dvh] w-full flex-col items-center justify-center gap-3 p-8 text-center">
      {!standingBy && (
        <div
          className="border-muted-foreground/30 border-t-foreground size-6 animate-spin rounded-full border-2"
          aria-hidden
        />
      )}
      <p className="text-foreground text-base font-semibold">
        {offline ? t`No internet connection` : standingBy ? t`Something went wrong` : t`Reconnecting…`}
      </p>
      <p className="text-muted-foreground max-w-sm text-sm">
        {offline
          ? t`We’ll load this page automatically as soon as you’re back online.`
          : standingBy
            ? t`We couldn’t load this page. Please try again.`
            : t`This is taking longer than usual. We’ll keep trying to load this page.`}
      </p>
      <ButtonPrimitive
        type="button"
        onClick={retry}
        className={cn('bg-primary text-primary-foreground mt-1 rounded-md px-4 py-2 text-sm font-medium')}
      >
        {t`Try again`}
      </ButtonPrimitive>
    </div>
  )
}
